import { Mark } from '@tiptap/core';

/**
 * CommentMark — TipTap Mark extension for inline comment highlights.
 *
 * Stores commentId directly in the document (vs. external Decoration tracking).
 * Marks travel with text automatically through Yjs — zero position-tracking code.
 */
const CommentMark = Mark.create({
  name: 'comment',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      commentId: {
        default: null,
        parseHTML: el => el.getAttribute('data-comment-id'),
        renderHTML: attrs => attrs.commentId ? { 'data-comment-id': attrs.commentId } : {},
      },
      resolved: {
        default: false,
        parseHTML: el => el.getAttribute('data-resolved') === 'true',
        renderHTML: attrs => ({ 'data-resolved': String(attrs.resolved) }),
      },
    };
  },

  // Don't extend the mark when typing at the boundary
  inclusive: false,

  parseHTML() {
    return [{ tag: 'span[data-comment-id]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const resolved = HTMLAttributes['data-resolved'] === 'true';
    return [
      'span',
      {
        ...HTMLAttributes,
        class: `comment-mark${resolved ? ' resolved' : ''}`,
      },
    ];
  },

  addCommands() {
    return {
      /**
       * Wrap current selection with a comment mark.
       * @param {string} commentId
       */
      addCommentMark:
        (commentId) =>
        ({ commands }) => {
          return commands.setMark(this.name, { commentId, resolved: false });
        },

      /**
       * Remove all comment marks with the given commentId from the document.
       * @param {string} commentId
       */
      removeCommentMark:
        (commentId) =>
        ({ tr, state, dispatch }) => {
          const { doc } = state;
          let changed = false;

          doc.descendants((node, pos) => {
            if (!node.isText) return;
            node.marks.forEach(mark => {
              if (mark.type.name === 'comment' && mark.attrs.commentId === commentId) {
                tr.removeMark(pos, pos + node.nodeSize, mark.type);
                changed = true;
              }
            });
          });

          if (changed && dispatch) {
            dispatch(tr);
            return true;
          }
          return false;
        },

      /**
       * Toggle resolved state on all comment marks with the given commentId.
       * @param {string} commentId
       * @param {boolean} resolved
       */
      setCommentResolved:
        (commentId, resolved) =>
        ({ tr, state, dispatch }) => {
          const { doc } = state;
          let changed = false;

          doc.descendants((node, pos) => {
            if (!node.isText) return;
            node.marks.forEach(mark => {
              if (mark.type.name === 'comment' && mark.attrs.commentId === commentId) {
                tr.addMark(
                  pos,
                  pos + node.nodeSize,
                  mark.type.create({ ...mark.attrs, resolved }),
                );
                changed = true;
              }
            });
          });

          if (changed && dispatch) {
            dispatch(tr);
            return true;
          }
          return false;
        },

      /**
       * Rename a comment mark in-place: update the commentId attribute on all
       * matching marks without removing/re-adding them.
       * Used in the ACK handler to switch from tempId → permanentId while keeping
       * the mark positioned correctly in the document.
       */
      renameCommentMark:
        (oldId, newId) =>
        ({ tr, state, dispatch }) => {
          const markType = state.schema.marks.comment;
          if (!markType) return false;

          let changed = false;
          state.doc.descendants((node, pos) => {
            if (!node.isText) return;
            node.marks.forEach(mark => {
              if (mark.type === markType && mark.attrs.commentId === oldId) {
                tr.removeMark(pos, pos + node.nodeSize, markType);
                tr.addMark(pos, pos + node.nodeSize, markType.create({
                  commentId: newId,
                  resolved: mark.attrs.resolved,
                }));
                changed = true;
              }
            });
          });

          if (changed && dispatch) {
            dispatch(tr);
            return true;
          }
          return false;
        },
    };
  },
});

export default CommentMark;
